import { Button } from 'antd'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { getCookie } from 'cookies-next'
import styled from 'styled-components'
import Image from 'next/image'

import { getLanguage } from '@/lib/text-utils'
import { poppins } from '@/components/common/fonts'
import { Model } from '@/types/models'
import { Gender } from '@/constants/gender'
import { AudioOutlined } from '@ant-design/icons'

import { VoiceSelectorModal } from './VoiceSelectorModal'

type VoiceButtonProps = {
  voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }
  onChangeVoice: (voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }) => void

  ttsList: Tts[]
  models: Model[]

  className?: string
}

export const VoiceButton: FC<VoiceButtonProps> = ({
  voice,
  onChangeVoice,

  ttsList,
  models,

  className,
}) => {
  const { t } = useTranslation('common')

  const locale = (getCookie('NEXT_LOCALE') || 'en') as string

  const [isMounted, setIsMounted] = useState<boolean>(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  const { language, isOriginalVoice } = voice

  const voiceText = useMemo(() => {
    const languageText = getLanguage(language, locale)

    if (isOriginalVoice) {
      return `${languageText} - ${t('Original Voice')}`
    }

    return languageText
  }, [t, language, isOriginalVoice, locale])

  const [isOpenedVoiceModal, setIsOpenedVoiceModal] = useState<boolean>(false)

  const handleSelectVoice = (_voice: {
    modelId: string
    voiceId: string
    language: string
    gender: Gender
    isOriginalVoice: boolean
    type: string
  }) => {
    onChangeVoice(_voice)
    setIsOpenedVoiceModal(false)
  }

  if (!isMounted) {
    return null
  }

  return (
    <>
      <VoiceButtonContainer
        className={className}
        title={voiceText}
        onClick={() => {
          setIsOpenedVoiceModal(true)
        }}
      >
        <FlagImage language={language} />
        <LanguageText>{voiceText}</LanguageText>
      </VoiceButtonContainer>

      <VoiceSelectorModal
        isOpened={isOpenedVoiceModal}
        onClose={() => {
          setIsOpenedVoiceModal(false)
        }}
        onSelect={handleSelectVoice}
        voice={voice}
        ttsList={ttsList}
        models={models}
      />
    </>
  )
}

const VoiceButtonContainer = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;

  height: 40px;
  max-width: 100%;

  font-family: ${poppins.string};
  font-size: 14px;
  line-height: 14px;
  color: #222222;
`

const FlagImage: FC<{
  language: string
}> = ({ language }) => {
  const isMultipleLanguage = language === 'multi'

  const flagSrc = useMemo(() => {
    if (isMultipleLanguage) {
      return ''
    }

    const [languageCode, countryCode] = language.toLocaleLowerCase().split('-')

    if (!countryCode) {
      return `/images/flags/language/${languageCode}.svg`
    }

    return `/images/flags/country/${countryCode}.svg`
  }, [isMultipleLanguage, language])

  return (
    <>
      {!isMultipleLanguage && flagSrc && (
        <Image src={flagSrc} alt={language} width={22} height={13} />
      )}

      {isMultipleLanguage && <AudioOutlined />}
    </>
  )
}

const LanguageText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;

  line-height: 1.5;
`
